import {
  createFileRoute,
  Link,
  type ParsedLocation,
  useSearch,
} from "@tanstack/react-router";
import { useEffect, useState, type JSX } from "react";
import { z } from "zod";

import DoorEnter from "@repo/icons/default/DoorEnter";
import { CpsSpinner } from "corpus";

import {
  EmptyStateButton,
  EmptyStateDescription,
  EmptyStateIcon,
  EmptyStateRoot,
  EmptyStateTitle,
} from "@/components/ui/empty-state/empty-state";
import { HeaderRoot, HeaderTitle } from "@/components/ui/layout/header/header";
import { Page } from "@/components/ui/layout/page/page";
import { logLoginRequestedFinished, logLoginStarted } from "@/lib/breadcrumbs";
import type { RootRouteContext } from "@/routes/-root/-root-context";

const beforeLoad = async (
  context: RootRouteContext,
  location: ParsedLocation,
  search: LoginRedirectionSchema,
): Promise<void> => {
  logLoginStarted({ location, retry: search.retry });

  await context.auth0AuthenticationClient.loginWithRedirect(
    search.returnTo,
    search.retry,
  );

  logLoginRequestedFinished({ location });
};

const LoginPendingPage = (): JSX.Element => {
  const search = useSearch({ from: "/auth/login" });
  const [showButton, setShowButton] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setShowButton(true), 3000);
    return () => clearTimeout(timer);
  }, []);

  return (
    <>
      <HeaderRoot>
        <HeaderTitle title="Livance" align="center" />
      </HeaderRoot>
      <Page>
        <EmptyStateRoot>
          <EmptyStateIcon icon={DoorEnter} />
          <EmptyStateTitle>Entrando no seu consultório</EmptyStateTitle>
          <EmptyStateDescription>
            Você será redirecionado para a tela de login, caso nada aconteça, use o botão
            abaixo.
          </EmptyStateDescription>
          {showButton ? (
            <Link
              to="/"
              preload={false}
              search={{ ...search, retry: search.retry ? search.retry + 1 : 0 }}
              className="w-full"
            >
              <EmptyStateButton fullWidth>Ir para a tela de login</EmptyStateButton>
            </Link>
          ) : (
            <CpsSpinner />
          )}
        </EmptyStateRoot>
      </Page>
    </>
  );
};

const loginRedirectionSearch = z.object({
  returnTo: z.string().default("/"),
  retry: z.number().optional().catch(0),
});

type LoginRedirectionSchema = z.output<typeof loginRedirectionSearch>;

export const Route = createFileRoute("/auth/login")({
  component: LoginPendingPage,
  validateSearch: loginRedirectionSearch,
  beforeLoad: async ({ context, location, search }) => {
    await beforeLoad(context, location, search);
  },
});
